/**
 * The provider tree and i18n instance shared by the two ways this app is
 * mounted:
 *
 * - `entry.client.tsx` — as an OpenComponents MFE, rendered by an Evo host.
 * - `standalone/main.tsx` — as the whole page of a static site, which is how it
 *   reaches Evo as a Native Tab widget (see `infrastructure/README.md`).
 *
 * Both paths need the same providers, so the tree lives here and each entry
 * only supplies what differs: the shadow root, the instance id, the backend and
 * where `resources.json` is served from.
 */
import i18next, { type i18n } from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import {
  BackendProvider,
  InstanceProvider,
  LanguageSwitcher,
  ShadowRootProvider,
} from '@access-evo/evo-api';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { CHATOBI_BACKEND_ID } from './evo/backend';

const queryClient = new QueryClient();

type ChatobiAppProps = {
  i18n: i18n;
  instanceId?: string;
  /** `null` when the app owns the document and needs no shadow root. */
  shadowRoot: ShadowRoot | null;
  /** Defaults to the BFF proxy, which is what Evo provides. */
  backendName?: string;
  /** Only used by backends that are not resolved from the Evo registry. */
  baseUrl?: string;
};

export function ChatobiApp({
  i18n,
  instanceId,
  shadowRoot,
  backendName = 'evo-bff-proxy',
  baseUrl,
}: ChatobiAppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <InstanceProvider instanceId={instanceId}>
        <I18nextProvider i18n={i18n}>
          <LanguageSwitcher i18n={i18n}>
            <ShadowRootProvider shadowRoot={shadowRoot}>
              <BackendProvider
                name={backendName}
                backendId={CHATOBI_BACKEND_ID}
                baseUrl={baseUrl}
              >
                <App />
              </BackendProvider>
            </ShadowRootProvider>
          </LanguageSwitcher>
        </I18nextProvider>
      </InstanceProvider>
    </QueryClientProvider>
  );
}

export async function createI18nInstance(resourcesUrl: string) {
  let resources = {};
  try {
    const response = await fetch(resourcesUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch resources: ${response.status} ${response.statusText}`);
    }
    resources = await response.json();
  } catch (error) {
    console.error('Failed to load i18n resources, initializing with empty resources:', error);
  }

  const mfeI18n = i18next.createInstance();
  await mfeI18n.use(initReactI18next).init({
    lng: 'en-GB',
    fallbackLng: 'en-GB',
    debug: false,
    interpolation: {
      escapeValue: false, // React already does escaping
    },
    resources,
  });
  return mfeI18n;
}
